import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, Plus, CopySimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublisherEditions() {
  const { publisherId } = useParams();
  const [editions, setEditions] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('editions');
  const [showCreate, setShowCreate] = useState(false);
  const [edForm, setEdForm] = useState({ name: '', description: '', game_id: '', year: '', is_current: false });
  const [exForm, setExForm] = useState({ name: '', description: '', game_id: '', standalone: false });

  const fetch = () => {
    Promise.all([
      axios.get(`${API_URL}/api/publisher-portal/${publisherId}/editions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/publisher-portal/${publisherId}/expansions`, { withCredentials: true }),
      axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true }),
    ]).then(([ed, ex, g]) => {
      setEditions(ed.data || []); setExpansions(ex.data || []); setGames(g.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(fetch, [publisherId]);

  const createEdition = async () => {
    if (!edForm.name || !edForm.game_id) { toast.error('Name and game required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/editions`, edForm, { withCredentials: true });
      toast.success('Edition created'); setShowCreate(false); setEdForm({ name: '', description: '', game_id: '', year: '', is_current: false }); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const createExpansion = async () => {
    if (!exForm.name || !exForm.game_id) { toast.error('Name and game required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/expansions`, exForm, { withCredentials: true });
      toast.success('Expansion created'); setShowCreate(false); setExForm({ name: '', description: '', game_id: '', standalone: false }); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#9D4CDD]/50 focus:outline-none placeholder-[#444]";
  const getGameName = (gid) => games.find(g => g.id === gid)?.name || '';

  return (
    <div data-testid="publisher-editions-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Editions & Expansions</h1>
          <p className="text-sm text-[#666] mt-1">{editions.length} editions, {expansions.length} expansions</p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm"
          data-testid="create-edition-btn">
          <Plus size={16} weight="bold" /> Add
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 mb-6 bg-white/[0.02] rounded-lg p-1 w-fit">
        {['editions', 'expansions'].map(t => (
          <button key={t} onClick={() => { setTab(t); setShowCreate(false); }}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
              tab === t ? 'bg-[#9D4CDD]/15 text-[#9D4CDD]' : 'text-[#666] hover:text-white'
            }`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {showCreate && tab === 'editions' && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-3">
          <p className="text-sm text-white font-semibold">New Edition</p>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Edition name" value={edForm.name} onChange={e => setEdForm({ ...edForm, name: e.target.value })} data-testid="edition-name-input" />
            <select className={inputCls} value={edForm.game_id} onChange={e => setEdForm({ ...edForm, game_id: e.target.value })}>
              <option value="">Select game...</option>{games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Year (e.g. 2025)" value={edForm.year} onChange={e => setEdForm({ ...edForm, year: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-[#888]"><input type="checkbox" checked={edForm.is_current} onChange={e => setEdForm({ ...edForm, is_current: e.target.checked })} className="accent-[#9D4CDD]" /> Current edition</label>
          </div>
          <input className={inputCls} placeholder="Description" value={edForm.description} onChange={e => setEdForm({ ...edForm, description: e.target.value })} />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={createEdition} className="px-5 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="edition-submit-btn">Create</button>
          </div>
        </div>
      )}

      {showCreate && tab === 'expansions' && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-3">
          <p className="text-sm text-white font-semibold">New Expansion</p>
          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls} placeholder="Expansion name" value={exForm.name} onChange={e => setExForm({ ...exForm, name: e.target.value })} data-testid="expansion-name-input" />
            <select className={inputCls} value={exForm.game_id} onChange={e => setExForm({ ...exForm, game_id: e.target.value })}>
              <option value="">Select game...</option>{games.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          </div>
          <input className={inputCls} placeholder="Description" value={exForm.description} onChange={e => setExForm({ ...exForm, description: e.target.value })} />
          <label className="flex items-center gap-2 text-sm text-[#888]"><input type="checkbox" checked={exForm.standalone} onChange={e => setExForm({ ...exForm, standalone: e.target.checked })} className="accent-[#9D4CDD]" /> Standalone expansion</label>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
            <button onClick={createExpansion} className="px-5 py-2 rounded-lg bg-[#9D4CDD] text-white font-semibold text-sm" data-testid="expansion-submit-btn">Create</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />) :
        tab === 'editions' ? (
          editions.length === 0 ? (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-8 text-center">
              <Package size={40} className="mx-auto text-[#333] mb-3" /><p className="text-white font-medium">No Editions</p>
            </div>
          ) : editions.map(e => (
            <div key={e.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CopySimple size={20} weight="duotone" className="text-[#9D4CDD]" />
                <div>
                  <p className="text-white font-medium">{e.name} {e.is_current && <span className="text-[10px] text-[#10B981] ml-1">Current</span>}</p>
                  <div className="text-[11px] text-[#555]">{getGameName(e.game_id)} {e.year && `· ${e.year}`}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          expansions.length === 0 ? (
            <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-8 text-center">
              <Package size={40} className="mx-auto text-[#333] mb-3" /><p className="text-white font-medium">No Expansions</p>
            </div>
          ) : expansions.map(e => (
            <div key={e.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package size={20} weight="duotone" className="text-[#3B82F6]" />
                <div>
                  <p className="text-white font-medium">{e.name} {e.standalone && <span className="text-[10px] text-[#F59E0B] ml-1">Standalone</span>}</p>
                  <div className="text-[11px] text-[#555]">{getGameName(e.game_id)}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
